import React, { useState } from 'react';
import { auth } from '@shared/services/firebase';
import { signInWithEmailAndPassword, signOut } from "firebase/auth";

interface Props {
  onLogin: () => void;
  onSwitchToRegister: () => void;
}

const Login: React.FC<Props> = ({ onLogin, onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      if (!userCredential.user.emailVerified) {
        setError('Please verify your email before logging in. Check your inbox.');
        await signOut(auth);
        setLoading(false);
        return;
      }

      window.location.href = '/portal';
    } catch (err: any) {
      console.error("Auth Error:", err);
      let message = err.message || 'An error occurred during authentication.';

      if (err.code === 'auth/wrong-password') message = 'Incorrect password.';
      if (err.code === 'auth/user-not-found') message = 'No account found with this email.';
      if (err.code === 'auth/invalid-email') message = 'Invalid email address format.';
      if (err.code === 'auth/network-request-failed') message = 'Network error. Please check your connection.';

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-6 md:p-12 min-h-[600px] bg-[#f8fafc]">
      <div className="w-20 h-20 bg-white rounded-[1.75rem] flex items-center justify-center mb-8 shadow-2xl shadow-slate-200 animate-in zoom-in duration-500 overflow-hidden">
        <img src="/Logo1.png" alt="Logo" className="w-full h-full object-cover" />
      </div>

      <div className="w-full max-w-md bg-white p-10 rounded-[3rem] border border-slate-100 shadow-2xl shadow-slate-200/40">
        <h2 className="text-3xl font-black text-slate-900 mb-2 text-center tracking-tight">
          Doctor Login
        </h2>
        <p className="text-slate-500 text-center mb-10 font-medium text-sm">
          Access your performance tracks and evidence.
        </p>

        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold border border-red-100 flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span className="break-words">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="group">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2.5 ml-4 group-focus-within:text-blue-600 transition-colors">NHS / Private Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="doctor@nhs.net"
              className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold text-slate-700 transition-all placeholder:text-slate-300"
            />
          </div>

          <div className="group">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2.5 ml-4 group-focus-within:text-blue-600 transition-colors">Secure Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold text-slate-700 transition-all placeholder:text-slate-300"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black shadow-xl shadow-slate-200 hover:bg-blue-600 transition-all active:scale-[0.98] mt-6 flex justify-center items-center group overflow-hidden relative"
          >
            {loading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <span className="flex items-center gap-2">
                Sign In to Dashboard
                <svg className="group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 18 15 12 9 6"/></svg>
              </span>
            )}
          </button>
        </form>

        <div className="mt-10 text-center border-t border-slate-100 pt-8">
          <button
            onClick={onSwitchToRegister}
            className="text-sm font-bold text-slate-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 mx-auto"
          >
            New to NHSInterview? Create Profile
          </button>
        </div>
      </div>

      <p className="mt-12 text-[10px] font-black text-slate-300 uppercase tracking-widest">
        Secured by Google Identity Services
      </p>
    </div>
  );
};

export default Login;
